/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: Domain
 * Reason: Explains one Provisionssatz (cp.ProvisionContractAgent QL row) in the agent's
 *         Provisionssätze table: which base the factor was applied to (monthly base for
 *         MONTHLY_PREMIUM, total-term base for PER_MILLE) and WHY a row carries no or an
 *         overwritten amount (overwrite reason, e.g. M-026; factor 0; base 0). Pure functions,
 *         read by the two `#cust_` columns of CpProvisionAgentContractAgentsComponent.
 */
import {ProvisionMatrixCalcMode} from '../../enum/provision-matrix-calc-mode.enum';
import {cpRowValue} from './cp-provision-assignment-columns';
import {CpAgentRateBasis} from '../../interface/cp-agent-rate-basis.interface';
import {CpAgentRateReason} from '../../interface/cp-agent-rate-reason.interface';

/** fieldId of the custom „Basis" column; the layout field is `#cust_rateBasis`. */
export const CP_AGENT_RATE_BASIS_FIELD_ID: string = 'rateBasis';

/** fieldId of the custom „Grund" column; the layout field is `#cust_rateReason`. */
export const CP_AGENT_RATE_REASON_FIELD_ID: string = 'rateReason';

/**
 * The calculation mode of a QL row — the pipe may deliver the ordinal or the constant name.
 * Anything unreadable counts as MONTHLY_PREMIUM, the mode of every Rathje matrix today.
 */
export function cpAgentRateIsPerMille(object: any): boolean {
    const mode = cpRowValue(object, 'e.appliedCalcMode');
    if (typeof mode === 'number') {
        return mode === ProvisionMatrixCalcMode.PER_MILLE;
    }
    return typeof mode === 'string' && mode.trim().toUpperCase() === 'PER_MILLE';
}

/**
 * The base the applied factor multiplies: `baseAmountMonthly` for MONTHLY_PREMIUM,
 * `baseAmountTotal` for PER_MILLE (ProvisionContractAgent field docs: the total base is only
 * relevant for PER_MILLE). In DIFFERENCE mode the monthly base already IS the difference.
 */
export function cpAgentRateBasis(object: any): CpAgentRateBasis {
    const perMille = cpAgentRateIsPerMille(object);
    const raw = cpRowValue(object, perMille ? 'e.baseAmountTotal' : 'e.baseAmountMonthly');
    return {value: cpAgentToNumber(raw), perMille};
}

/**
 * Why a Satz pays what it pays — or nothing. In this order, the first that applies:
 *   1. an overwrite with its stored reason (M-026 „negativ gerechneter Provisionsbetrag" and every
 *      manual overwrite carry one; the backend makes the reason mandatory with the overwrite),
 *   2. factor 0 — the matrix row deliberately pays nothing (a „Nullmatrix"); on a positive base
 *      without overwrite the text names backend message M-055, which the engine raises for it,
 *   3. base 0 — there was no commission-relevant premium (difference 0, premium-free),
 *   4. amount 0 without one of the above — stated as such rather than left blank.
 * `null` for a regular row with an amount.
 */
export function cpAgentRateReason(object: any): CpAgentRateReason | null {

    const overwriteReason = cpRowValue(object, 'e.overwriteReason');
    if (typeof overwriteReason === 'string' && overwriteReason.trim().length) {
        return {text: overwriteReason.trim(), severity: 'warn'};
    }

    const amount = cpAgentToNumber(cpRowValue(object, 'e.provisionAmount'));
    const overwrite = cpAgentToNumber(cpRowValue(object, 'e.provisionAmountOverwrite'));
    const effective = overwrite ?? amount;

    if (effective !== null && effective !== 0) {
        return null;
    }

    const factor = cpAgentToNumber(cpRowValue(object, 'e.appliedFactor'));
    const basis = cpAgentRateBasis(object).value;
    if (factor === 0) {
        // The engine raises M-055 for exactly this case: factor 0 on a positive base, no overwrite.
        if (basis !== null && basis > 0 && overwrite === null) {
            return {text: 'Faktor 0 laut Matrix (Meldung M-055) — die Matrixzeile sieht keine Provision vor.', severity: 'info'};
        }
        return {text: 'Faktor 0 — die Matrixzeile sieht keine Provision vor.', severity: 'info'};
    }

    if (basis === 0) {
        return {text: 'Basis 0 — kein provisionsrelevanter Beitrag bzw. keine Beitragsdifferenz.', severity: 'info'};
    }

    if (effective === null) {
        return {text: 'Noch kein Betrag berechnet.', severity: 'secondary'};
    }

    return {text: 'Betrag 0,00 € ohne gespeicherten Grund — Berechnung im Satz prüfen.', severity: 'secondary'};
}

/** A QL numeric value (number or numeric string) as a number, `null` for anything else. */
export function cpAgentToNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}
